import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';

/**
 * Single line-item row in the Manual Invoice dialog.
 * Extracted into its own component (with flat props) so the visual-edits
 * babel plugin doesn't recurse infinitely on a nested .map() inside setForm.
 *
 * F#4 — added Product Type select. The native <select> renders cleanly in
 * 1-line height matching the surrounding <Input>s without dragging in
 * Radix Select overhead (this dialog already has 5+ inputs per row).
 */
const PRODUCT_TYPES = ['', 'Flight', 'Hotel', 'Visa', 'Activity', 'Tours', 'Misc', 'Package'];

// Session F (Task F2) — per-productType reference field label. Fixed for
// every type except Misc, where the agent supplies their own label (stored
// in referenceLabel) since there's no universal field name for an
// arbitrary "Misc" line.
const REFERENCE_LABELS = {
  Flight: 'PNR',
  Hotel: 'HCN (Hotel Confirmation Number)',
  Visa: 'Application/Reference Number',
  Activity: 'Booking Reference',
  Tours: 'Booking Reference',
  Misc: 'Reference',
};

export default function ManualInvoiceLineItem({
  index,
  description = '',
  destination = '',
  quantity = 1,
  amount = 0,
  productType = '',
  hsnCode = '',
  referenceNumber = '',
  referenceLabel = '',
  onChangeField,
  onRemove,
  canRemove,
}) {
  const refLabel = REFERENCE_LABELS[productType];
  return (
    <div className="p-3 bg-slate-50 rounded-lg space-y-2" data-testid={`manual-item-${index}`}>
    <div className="grid grid-cols-12 gap-2 items-start">
      <Input
        className="col-span-3"
        placeholder="Description (e.g. Bali Holiday Package)"
        value={description}
        onChange={(e) => onChangeField(index, 'description', e.target.value)}
      />
      {/* F#4 — Product Type */}
      <select
        className="col-span-2 h-10 px-2 rounded-md border border-slate-200 bg-white text-sm"
        value={productType}
        onChange={(e) => onChangeField(index, 'productType', e.target.value)}
        data-testid={`manual-item-${index}-product-type`}
      >
        {PRODUCT_TYPES.map((p) => (
          <option key={p} value={p}>{p || '— Type —'}</option>
        ))}
      </select>
      <Input
        className="col-span-2"
        placeholder="Destination"
        value={destination}
        onChange={(e) => onChangeField(index, 'destination', e.target.value)}
      />
      {/* F#6 — HSN / SAC code per line. Defaults to 998555 at render time
          when blank. Kept short (col-span-1) — typical SAC is 6 digits. */}
      <Input
        className="col-span-1 font-mono"
        placeholder="HSN"
        value={hsnCode}
        onChange={(e) => onChangeField(index, 'hsnCode', e.target.value)}
        data-testid={`manual-item-${index}-hsn`}
      />
      <Input
        className="col-span-1"
        type="number"
        min="1"
        value={quantity}
        onChange={(e) => onChangeField(index, 'quantity', e.target.value)}
      />
      <Input
        className="col-span-2"
        type="number"
        min="0"
        placeholder="Amount"
        value={amount}
        onChange={(e) => onChangeField(index, 'amount', e.target.value)}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="col-span-1 text-red-500"
        onClick={() => onRemove(index)}
        disabled={!canRemove}
        data-testid={`manual-item-${index}-remove`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
      {refLabel && (
        <div className="grid grid-cols-12 gap-2" data-testid={`manual-item-${index}-reference-row`}>
          {productType === 'Misc' && (
            <Input
              className="col-span-3"
              placeholder="Reference label"
              value={referenceLabel || 'Reference'}
              onChange={(e) => onChangeField(index, 'referenceLabel', e.target.value)}
              data-testid={`manual-item-${index}-reference-label`}
            />
          )}
          <Input
            className="col-span-4"
            placeholder={productType === 'Misc' ? (referenceLabel || 'Reference') : refLabel}
            value={referenceNumber}
            onChange={(e) => onChangeField(index, 'referenceNumber', e.target.value)}
            data-testid={`manual-item-${index}-reference`}
          />
        </div>
      )}
    </div>
  );
}
